import React from "react";
import { Badge } from "primereact/badge";
import { css, cx } from "@emotion/css";
import { isEmpty } from "lodash";
import AlpOutboundLoadStatus from "../../alpOutboundLoads/partials/AlpOutboundLoadStatus";

type ChipTone = {
    color: string;
    background: string;
    border: string;
};

/** Trained status hues — soft fill + saturated label (readable, compact tag) */
const CHIP_TONE_MAP: Record<string, ChipTone> = {
    "text-primary-500": {
        color: "#2563eb",
        background: "rgba(37, 99, 235, 0.12)",
        border: "rgba(37, 99, 235, 0.22)",
    },
    "text-green-500": {
        color: "#16a34a",
        background: "rgba(22, 163, 74, 0.12)",
        border: "rgba(22, 163, 74, 0.22)",
    },
    "text-red-500": {
        color: "#dc2626",
        background: "rgba(220, 38, 38, 0.11)",
        border: "rgba(220, 38, 38, 0.22)",
    },
    "text-yellow-500": {
        color: "#b45309",
        background: "rgba(234, 179, 8, 0.18)",
        border: "rgba(202, 138, 4, 0.28)",
    },
    "text-black-500": {
        color: "#475569",
        background: "rgba(100, 116, 139, 0.12)",
        border: "rgba(100, 116, 139, 0.22)",
    },
};

const DEFAULT_TONE = CHIP_TONE_MAP["text-black-500"];

const baseChipClass = css({
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "100%",
    width: "fit-content",
    padding: "0.15rem 0.55rem",
    borderRadius: "999px",
    borderWidth: 1,
    borderStyle: "solid",
    fontSize: "0.6875rem",
    fontWeight: 600,
    lineHeight: 1.35,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    whiteSpace: "normal",
    wordBreak: "break-word",
});

function chipToneClass(tone: ChipTone, clickable?: boolean) {
    return css({
        color: tone.color,
        backgroundColor: tone.background,
        borderColor: tone.border,
        ...(clickable ? {
            cursor: "pointer",
            transition: "filter 0.15s ease, box-shadow 0.15s ease",
            "&:hover": {
                filter: "brightness(0.98)",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",
            },
        } : {}),
    });
}

interface LoadStatusChipProps {
    colorClass: string;
    children: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLSpanElement>) => void;
    className?: string;
}

export function LoadStatusChip(props: LoadStatusChipProps) {
    const { colorClass, children, onClick, className } = props;
    const tone = CHIP_TONE_MAP[colorClass] ?? DEFAULT_TONE;

    return (
        <span
            className={ cx(
                "load-status-chip",
                baseChipClass,
                chipToneClass(tone, !!onClick),
                className
            ) }
            onClick={ onClick }
            role={ onClick ? "button" : undefined }
        >
            { children }
        </span>
    );
}

interface StatusDisplayProps {
    chip?: boolean;
    textable?: boolean | number;
    colorClass: string;
    label: string;
    badgeSeverity: "info" | "success" | "warning" | "danger";
}

export function StatusDisplay(props: StatusDisplayProps) {
    const { chip, textable, colorClass, label, badgeSeverity } = props;

    if ( chip ) {
        return <LoadStatusChip colorClass={ colorClass }>{ label }</LoadStatusChip>;
    }

    if ( textable ) {
        return <span className={ colorClass }>{ label }</span>;
    }

    return <Badge severity={ badgeSeverity } value={ label } />;
}

export function LoadStatusFlagChip(props: { colorClass: string; label: string }) {
    return (
        <div>
            <LoadStatusChip colorClass={ props.colorClass }>{ props.label }</LoadStatusChip>
        </div>
    );
}

export function OutboundLoadStatusChip(props: { status?: string | null }) {
    if ( isEmpty(props.status) ) {
        return <span>-</span>;
    }

    return (
        <div>
            <AlpOutboundLoadStatus status={ props.status } chip={ true } />
        </div>
    );
}

export function GateOutStatusChip(props: { confirmedAt?: string | null; t: (key: string) => string; dateLabel?: string | null }) {
    const { confirmedAt, t, dateLabel } = props;

    if ( !isEmpty(confirmedAt) ) {
        return (
            <div className="d-flex flex-column gap-1">
                <LoadStatusFlagChip colorClass="text-green-500" label={ t('gate out completed') } />
                { dateLabel && <div className="text-muted small">{ dateLabel }</div> }
            </div>
        );
    }

    return <LoadStatusFlagChip colorClass="text-red-500" label={ t('gate out: pending') } />;
}

interface AlpOutboundLoadGroupStatusChipsProps {
    row: any;
    t: (key: string) => string;
    /** When true, show delivered/in-transit/gate-out counts whenever totalCount > 0 (Aviso list). */
    alwaysShowCounts?: boolean;
}

export function AlpOutboundLoadGroupStatusChips(props: AlpOutboundLoadGroupStatusChipsProps) {
    const { row, t, alwaysShowCounts = false } = props;
    const deliveredCount = Number(row.delivered_load_count || 0);
    const inTransitCount = Number(row.in_transit_load_count || 0);
    const gateOutPendingCount = Number(row.gate_out_pending_count || 0);
    const totalCount = Number(row.total_load_count || row.qty || 0);
    const showCounts = totalCount > 0 && (alwaysShowCounts || row.status === 'mixed');

    const countChips = showCounts ? (
        <>
            <LoadStatusFlagChip
                colorClass="text-green-500"
                label={ `${ t('delivered') }: ${ deliveredCount }/${ totalCount }` }
            />
            <LoadStatusFlagChip
                colorClass="text-yellow-500"
                label={ `${ t('in transit') }: ${ inTransitCount }/${ totalCount }` }
            />
            <LoadStatusFlagChip
                colorClass="text-red-500"
                label={ `${ t('gate out: pending') }: ${ gateOutPendingCount }/${ totalCount }` }
            />
        </>
    ) : null;

    if ( row.status === 'mixed' ) {
        return (
            <div className="d-flex flex-column gap-1">
                <LoadStatusFlagChip colorClass="text-red-500" label={ t('multiple') } />
                { countChips }
            </div>
        );
    }

    return (
        <div className="d-flex flex-column gap-1">
            <OutboundLoadStatusChip status={ row.status } />
            { countChips }
            {
                (
                    !isEmpty(row.alp_supplier_compound)
                    && row.status !== 'announced' && row.status !== 'rejected'
                    && isEmpty(row.gate_out_confirmed_at)
                ) &&
                <LoadStatusFlagChip colorClass="text-red-500" label={ t('gate out: pending') } />
            }
            {
                row.has_export_custom
                && <LoadStatusFlagChip colorClass="text-black-500" label={ t('custom export') } />
            }
        </div>
    );
}
